import { API_URL } from "../config/connect";

const getAuthHeaders = () => {
  const token = localStorage.getItem('google_id_token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * Get all stories with pagination
 * GET /api/v1/admin/stories
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1, min: 1)
 * @param {number} params.limit - Items per page (default: 10, min: 1, max: 100)
 * @param {boolean} params.is_active - Filter by active status
 * @returns {Promise<Object>} List of stories with pagination
 */
export const getStories = async (params = {}) => {
  try {
    let url = `${API_URL}/api/v1/admin/stories`;
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
    
    if (queryParams.toString()) {
      url += `?${queryParams.toString()}`;
    }

    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching stories:', error);
    throw error;
  }
};

/**
 * Get story by ID
 * GET /api/v1/admin/stories/{story_id}
 * @param {number} storyId - Story ID
 * @returns {Promise<Object>} Story details
 */
export const getStoryById = async (storyId) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/admin/stories/${storyId}`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching story details:', error);
    throw error;
  }
};

/**
 * Create a new story
 * POST /api/v1/admin/stories
 * @param {Object} storyData - Story data
 * @param {string} storyData.title - Default title (required)
 * @param {string} storyData.kz_title - Kazakh title (optional)
 * @param {string} storyData.eng_title - English title (optional)
 * @param {string} storyData.ru_title - Russian title (optional)
 * @param {string} storyData.ch_title - Chinese title (optional)
 * @param {string} storyData.image_url - Image URL (required)
 * @param {number} storyData.priority - Priority (default: 0)
 * @param {boolean} storyData.is_active - Active status (default: true)
 * @returns {Promise<Object>} Created story
 */
export const createStory = async (storyData) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/admin/stories`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(storyData)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error creating story:', error);
    throw error;
  }
};

/**
 * Update a story
 * PATCH /api/v1/admin/stories/{story_id}
 * @param {number} storyId - Story ID
 * @param {Object} storyData - Story data to update (all fields optional)
 * @returns {Promise<Object>} Updated story
 */
export const updateStory = async (storyId, storyData) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/admin/stories/${storyId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(storyData)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error updating story:', error);
    throw error;
  }
};

/**
 * Delete a story
 * DELETE /api/v1/admin/stories/{story_id}
 * @param {number} storyId - Story ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteStory = async (storyId) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/admin/stories/${storyId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error deleting story:', error);
    throw error;
  }
};

/**
 * Get Stories Analytics - Active Hours
 * GET /api/v1/stories/analytics/active-hours
 * @param {Object} params - Query parameters
 * @param {number} params.days - Number of days to analyze (1-30, default: 7)
 * @returns {Promise<Object>} Active hours analytics data
 */
export const getStoriesActiveHours = async (params = {}) => {
  try {
    let url = `${API_URL}/api/v1/stories/analytics/active-hours`;
    const queryParams = new URLSearchParams();
    
    if (params.days) queryParams.append('days', params.days);
    
    if (queryParams.toString()) {
      url += `?${queryParams.toString()}`;
    }

    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching active hours analytics:', error);
    throw error;
  }
};

/**
 * Get Stories Analytics - Popular Searches
 * GET /api/v1/stories/analytics/popular-searches
 * @param {Object} params - Query parameters
 * @param {number} params.days - Number of days to analyze (1-30, default: 7)
 * @param {number} params.limit - Number of results to return (1-100, default: 20)
 * @param {number} params.category_id - Filter by category ID
 * @returns {Promise<Object>} Popular searches analytics data
 */
export const getStoriesPopularSearches = async (params = {}) => {
  try {
    let url = `${API_URL}/api/v1/stories/analytics/popular-searches`;
    const queryParams = new URLSearchParams();
    
    if (params.days) queryParams.append('days', params.days);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.category_id) queryParams.append('category_id', params.category_id);
    
    if (queryParams.toString()) {
      url += `?${queryParams.toString()}`;
    }

    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching popular searches analytics:', error);
    throw error;
  }
};

/**
 * Get Stories Analytics - Category Interest
 * GET /api/v1/stories/analytics/category-interest
 * @param {Object} params - Query parameters
 * @param {number} params.days - Number of days to analyze (1-30, default: 7)
 * @returns {Promise<Object>} Category interest analytics data
 */
export const getStoriesCategoryInterest = async (params = {}) => {
  try {
    let url = `${API_URL}/api/v1/stories/analytics/category-interest`;
    const queryParams = new URLSearchParams();
    
    if (params.days) queryParams.append('days', params.days);
    
    if (queryParams.toString()) {
      url += `?${queryParams.toString()}`;
    }

    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching category interest analytics:', error);
    throw error;
  }
};

/**
 * Log Search Query
 * POST /api/v1/search/log
 * @param {Object} searchData - Search data
 * @param {string} searchData.query - Search query (required)
 * @param {number} searchData.category_id - Category ID (optional)
 * @param {number} searchData.subcategory_id - Subcategory ID (optional)
 * @returns {Promise<Object>} Log confirmation
 */
export const logSearchQuery = async (searchData) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/search/log`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(searchData)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error logging search query:', error);
    throw error;
  }
};



